import { parseFormula, formulaText } from './formulaExpression'

// Only recognize expressions that can be represented without changing their
// grouping. All other formulas stay in advanced mode and retain their text.
function numberOf (node) {
  if (!node) return null
  if (node.kind === 'number' && String(node.value).trim() !== '' && Number.isFinite(Number(node.value))) return Number(node.value)
  if (node.kind === 'unary' && ['+', '-'].includes(node.op)) {
    const number = numberOf(node.child)
    return number == null ? null : node.op === '-' ? -number : number
  }
  return null
}

// Recognize only a literal upper bound on one business variable. Other min()
// expressions stay in advanced mode so their meaning is never approximated.
function upperBoundOf (node) {
  if (node?.kind !== 'call' || node.name !== 'min' || node.args.length !== 2) return null
  for (const [data, limit] of [[node.args[0], node.args[1]], [node.args[1], node.args[0]]]) {
    const cap = numberOf(limit)
    if (data?.kind === 'variable' && cap != null && Math.abs(cap) <= 1e9) return { data, cap }
  }
  return null
}

function termOf (node, sign) {
  if (!node) return null
  if (node.kind === 'unary' && ['+', '-'].includes(node.op)) return termOf(node.child, node.op === '-' ? -sign : sign)
  const number = numberOf(node)
  if (number != null) return { type: 'constant', sign: number < 0 ? -sign : sign, value: String(Math.abs(number)), original: number >= 0 ? node : null }
  if (node.kind === 'variable') return { type: 'variable', sign, key: node.key, value: '1', original: node }
  const directBound = upperBoundOf(node)
  if (directBound) return { type: 'variable', sign, key: directBound.data.key, value: '1', cap: String(directBound.cap), original: node }
  if (node.kind === 'binary' && node.op === '*') {
    if (node.left.kind === 'variable' && node.right.kind === 'variable')
      return { type: 'product', sign, key: node.left.key, factorKey: node.right.key, original: node }
    const left = numberOf(node.left), right = numberOf(node.right)
    const variable = left != null && node.right.kind === 'variable' ? node.right : right != null && node.left.kind === 'variable' ? node.left : null
    const factor = left != null && variable === node.right ? left : right
    if (variable && factor != null) return { type: 'variable', sign: factor < 0 ? -sign : sign, key: variable.key, value: String(Math.abs(factor)), original: factor >= 0 ? node : null }
    const parts = []
    const collect = current => {
      if (current.kind === 'binary' && current.op === '*') { collect(current.left); collect(current.right) } else parts.push(current)
    }
    collect(node)
    const values = parts.map(numberOf)
    const others = parts.filter((_, i) => values[i] == null)
    const target = others.length === 1 ? others[0] : null
    const capped = upperBoundOf(target)
    const data = capped ? capped.data : target
    const product = values.filter(value => value != null).reduce((a, b) => a * b, 1)
    if (data?.kind === 'variable' && Number.isFinite(product) && product >= 0 && product <= 1e9 && values.filter(value => value != null).length)
      return { type: 'variable', sign, key: data.key, value: String(product), cap: capped ? String(capped.cap) : null, original: node }
  }
  return null
}

function linearTerms (node) {
  if (node && node.kind === 'binary' && ['+', '-'].includes(node.op)) {
    const left = linearTerms(node.left)
    const right = termOf(node.right, node.op === '-' ? -1 : 1)
    return left && right ? [...left, right] : null
  }
  const term = termOf(node, 1)
  return term ? [term] : null
}

export function quickFromFormula (source, kind, scope) {
  let node
  try { node = parseFormula(source) } catch (_) { return null }
  if (!node) return null
  if (scope === 'legacy-condition' && node.kind === 'binary' && ['>', '>=', '<', '<=', '==', '!='].includes(node.op) && node.left.kind === 'variable') {
    const threshold = numberOf(node.right)
    if (threshold != null && Math.abs(threshold) <= 1e9) return { type: 'condition', key: node.left.key, operator: node.op, threshold: String(threshold) }
  }
  if (kind === 'network' && scope === 'basic') {
    let rounded = node, limited = false
    if (node.kind === 'call' && node.name === 'min' && node.args.length === 2 && numberOf(node.args[0]) === 8) {
    const lower = node.args[1]
      rounded = lower?.kind === 'call' && lower.name === 'max' && lower.args.length === 2 && numberOf(lower.args[0]) === 1 ? lower.args[1] : null
      limited = !!rounded
    }
    const ratio = rounded?.kind === 'call' && rounded.name === 'round' && rounded.args.length === 1 ? rounded.args[0] : null
    if (ratio?.kind === 'binary' && ratio.op === '/' && ratio.left.kind === 'variable' && ['population', '人口', 'P'].includes(ratio.left.key) && numberOf(ratio.right) > 0) {
      return { type: 'population', populationPerGrid: String(numberOf(ratio.right)), limited }
    }
  }
  if (kind === 'network' && scope === 'geo' && node.kind === 'binary' && node.op === '*' &&
      node.left.kind === 'variable' && node.right.kind === 'variable' &&
      ((['rows', '行数'].includes(node.left.key) && ['cols', '列数'].includes(node.right.key)) ||
       (['cols', '列数'].includes(node.left.key) && ['rows', '行数'].includes(node.right.key)))) return { type: 'mapCells' }
  if (kind === 'network' && scope === 'score') {
    const factors = []
    const collect = current => { if (current.kind === 'binary' && current.op === '*') { collect(current.left); collect(current.right) } else factors.push(current) }
    collect(node)
    const ratio = factors.find(current => current.kind === 'binary' && current.op === '-' && numberOf(current.left) === 1 && current.right.kind === 'variable' && ['错误率', 'dispatchErrorRate'].includes(current.right.key))
    const coefficients = factors.filter(current => current !== ratio).map(numberOf)
    if (ratio && coefficients.length && coefficients.every(value => value != null && value >= 0)) {
      const maximum = coefficients.reduce((a, b) => a * b, 1)
      if (Number.isFinite(maximum) && maximum <= 1e9) return { type: 'accuracy', key: ratio.right.key, maxPoints: String(maximum) }
    }
  }
  let wrapper = ''
  if (node.kind === 'call' && node.name === 'min' && node.args.length === 2 && numberOf(node.args[0]) === 100 &&
      node.args[1]?.kind === 'call' && node.args[1].name === 'max' && node.args[1].args.length === 2 && numberOf(node.args[1].args[0]) === 0) {
    wrapper = 'range100'; node = node.args[1].args[1]
  } else if (node.kind === 'call' && node.name === 'max' && node.args.length === 2 && numberOf(node.args[0]) === 0) {
    wrapper = 'nonnegative'; node = node.args[1]
  }
  const terms = linearTerms(node)
  return terms && terms.length <= 20 ? { type: 'linear', wrapper, terms } : null
}

export function quickFormula (quick) {
  if (!quick) throw new Error('请选择计算方式')
  if (quick.type === 'condition') {
    const text = String(quick.threshold == null ? '' : quick.threshold).trim(), number = Number(text)
    if (!quick.key) throw new Error('请选择条件使用的业务数据')
    if (!['>', '>=', '<', '<=', '==', '!='].includes(quick.operator)) throw new Error('请选择有效的比较方式')
    if (!text || !Number.isFinite(number) || Math.abs(number) > 1e9) throw new Error('条件数值须为绝对值不超过10亿的有限数值')
    return formulaText({ kind: 'binary', op: quick.operator, left: { kind: 'variable', key: quick.key }, right: { kind: 'number', value: String(number) } })
  }
  if (quick.type === 'population') {
    const text = String(quick.populationPerGrid == null ? '' : quick.populationPerGrid).trim()
    const number = Number(text)
    if (!text || !Number.isFinite(number) || number <= 0 || number > 1e9) throw new Error('每个网格对应人口须为大于0且不超过10亿的数值')
    const rounded = 'round(population / ' + number + ')'
    return quick.limited ? 'min(8, max(1, ' + rounded + '))' : rounded
  }
  if (quick.type === 'mapCells') return 'rows * cols'
  if (quick.type === 'accuracy') {
    const text = String(quick.maxPoints == null ? '' : quick.maxPoints).trim(), number = Number(text)
    if (!text || !Number.isFinite(number) || number < 0 || number > 1e9) throw new Error('满额得分须为0～10亿的有限数值')
    return '(1 - ' + quick.key + ') * ' + number
  }
  if (quick.type !== 'linear' || !Array.isArray(quick.terms) || !quick.terms.length) throw new Error('请至少配置一项计算内容')
  let node = null
  for (const [index, term] of quick.terms.entries()) {
    const text = String(term.value == null ? '' : term.value).trim()
    const value = Number(text)
    if (term.type !== 'product' && (!text || !Number.isFinite(value) || value < 0 || value > 1e9)) throw new Error('第' + (index + 1) + '项数值须为0～10亿的有限数值')
    let cap = null
    if (term.type === 'variable' && term.cap != null) {
      const raw = String(term.cap).trim(); cap = Number(raw)
      if (!raw || !Number.isFinite(cap) || Math.abs(cap) > 1e9) throw new Error('第' + (index + 1) + '项取值上限须为绝对值不超过10亿的有限数值')
    }
    let item
    if (term.original && !term.edited) item = term.original
    else if (term.type === 'constant') item = { kind: 'number', value: String(value) }
    else {
      if (!term.key) throw new Error('请选择第' + (index + 1) + '项业务数据')
      item = { kind: 'variable', key: term.key }
      if (cap != null) item = { kind: 'call', name: 'min', args: [{ kind: 'number', value: String(cap) }, item] }
      if (term.type === 'product') {
        if (!term.factorKey) throw new Error('请选择第' + (index + 1) + '项的业务系数')
        item = { kind: 'binary', op: '*', left: item, right: { kind: 'variable', key: term.factorKey } }
      } else if (value !== 1) item = { kind: 'binary', op: '*', left: item, right: { kind: 'number', value: String(value) } }
    }
    if (!node) node = term.sign < 0 ? { kind: 'unary', op: '-', child: item } : item
    else node = { kind: 'binary', op: term.sign < 0 ? '-' : '+', left: node, right: item }
  }
  if (quick.wrapper === 'nonnegative') node = { kind: 'call', name: 'max', args: [{ kind: 'number', value: '0' }, node] }
  if (quick.wrapper === 'range100') node = { kind: 'call', name: 'min', args: [{ kind: 'number', value: '100' }, { kind: 'call', name: 'max', args: [{ kind: 'number', value: '0' }, node] }] }
  return formulaText(node)
}

export function formulaVariableKeys (source) {
  let node
  try { node = parseFormula(source) } catch (_) { return [] }
  const keys = []
  const walk = current => {
    if (!current) return
    if (current.kind === 'variable' && !keys.includes(current.key)) keys.push(current.key)
    if (current.kind === 'binary') { walk(current.left); walk(current.right) }
    if (current.kind === 'unary') walk(current.child)
    if (current.kind === 'call') current.args.forEach(walk)
  }
  walk(node)
  return keys
}
